import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@/shared/api/mutator/custom-instance';

const BASE = '/frontend/admin/ess-products';

export interface ESSProductSummary {
    glNo: string;
    productName: string;
    productCode: string;
    deductionCode: string;
    emkopoSyncStatus: 'SYNCED' | 'DECOMMISSIONED' | 'PENDING' | 'FAILED';
    emkopoLastSyncDate: string | null;
    emkopoLastSyncError: string | null;
    isActive: boolean;
}

interface PageResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

interface ListParams {
    page?: number;
    size?: number;
    status?: string;
}

const QUERY_KEY = 'ess-products';

export const useESSProducts = (params: ListParams = {}) => {
    const { page = 0, size = 20, status } = params;
    return useQuery({
        queryKey: [QUERY_KEY, page, size, status],
        queryFn: () =>
            customInstance<PageResponse<ESSProductSummary>>({
                url: BASE,
                method: 'GET',
                params: { page, size, ...(status ? { status } : {}) },
            }),
    });
};

export const useDecommissionProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ glNo, reason }: { glNo: string; reason?: string }) =>
            customInstance<{ success: boolean; message: string }>({
                url: `${BASE}/${glNo}/decommission`,
                method: 'POST',
                data: { reason },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
};

export const useSendProductDetail = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (glNo: string) =>
            customInstance<{ success: boolean; message: string }>({
                url: `${BASE}/${glNo}/send-detail`,
                method: 'POST',
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
};
